'use client'

import {
  Box,
  Collapsible,
  Stack,
  HStack,
  Button,
  Text,
  Flex,
} from '@chakra-ui/react'
import { IShipper } from '@/_types'
import { useLocale, useTranslations } from 'next-intl'
import { Clock, InfoIcon, ShoppingCart } from 'lucide-react'
import { LuChevronRight } from 'react-icons/lu'
import { useEffect, useState, type MouseEvent } from 'react'
import Image from 'next/image'
import { MenuItem } from '@/features/brand-menu/types'
import StickyBox from 'react-sticky-box'
import { useSelector } from 'react-redux'
import { IRedux } from '@/_store/types'
import { Link } from '@/i18n/navigation'
import { getBrandHref } from '@/_lib/brand-href'
import {
  categoryHref,
  isModifiedClick,
  rememberCategoryHash,
} from '../../categories/category-menu-link'

interface SidebarProps {
  shipper: IShipper
  zoneError: boolean
  menuItems: MenuItem[]
  activeSection: string | null
  onMenuClick?: (id: string) => void
  linkBase?: string
  maxHeight?: string
}

export default function Sidebar({
  shipper,
  zoneError,
  menuItems,
  activeSection,
  onMenuClick,
  linkBase,
  maxHeight,
}: SidebarProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const assetBaseUrl = process.env.BASE_URL || ''
  const isScrollable = Boolean(maxHeight)
  const isNavigateMode = Boolean(linkBase)

  const t = useTranslations()
  const locale = useLocale()
  const { branches } = useSelector((state: IRedux) => state.common)
  const branch = branches?.[shipper.id]

  // Keep the active category's parent expanded when the route/section changes.
  // Do not reset other sections to false on menuItems identity changes — that
  // caused a flash (default open → effect closes → effect re-opens active).
  useEffect(() => {
    if (!activeSection) return

    const parentWithChild = menuItems.find((parent) =>
      parent.children.some((child) => child.id === activeSection),
    )

    if (
      !parentWithChild &&
      !menuItems.some((item) => item.id === activeSection)
    )
      return

    const targetId = parentWithChild ? parentWithChild.id : activeSection
    setOpenSections((prev) => {
      if (prev[targetId]) return prev
      return { ...prev, [targetId]: true }
    })
  }, [activeSection, menuItems])

  const isSectionOpen = (item: MenuItem) => {
    if (openSections[item.id] !== undefined) {
      return openSections[item.id]
    }
    // First paint / unset: open only if this item is (or contains) the active section
    return (
      activeSection === item.id ||
      item.children.some((child) => child.id === activeSection)
    )
  }

  return (
    <Box
      as='aside'
      display={{ base: 'none', lg: 'block' }}
      flexShrink={0}
      position='relative' // Ensure container is relative context
      h='full'
    >
      <StickyBox offsetTop={120} offsetBottom={20}>
        <Box w='210px' pb={9}>
          <Stack gap={3} pr={isScrollable ? 2 : 0}>
            {!zoneError && (
              <>
                <Text fontSize='lg' fontWeight={600}>
                  {t('delivery')}
                </Text>
                <InfoRow
                  icon={<Clock size={16} />}
                  text={t('delivery_from_40_minutes')}
                />
                <InfoRow
                  icon={<ShoppingCart size={16} />}
                  text={
                    <Flex flexWrap='wrap'>
                      {t('restaurant_delivery_description')}
                      <HStack gap={1}>
                        <span> 18,000 UZS </span>
                        <Link
                          href={'/delivery?return_url=/brand/' + shipper.id}
                        >
                          <InfoIcon size={14} />
                        </Link>
                      </HStack>
                    </Flex>
                  }
                  preserveWhitespace
                />
              </>
            )}
            <Text fontSize='lg' fontWeight={600} mt={zoneError ? 0 : 3}>
              {t('reservations')}
            </Text>
            <Button fontSize='sm' rounded={12} size='xl' bg='primary' asChild>
              <Link
                href={getBrandHref(shipper.id, {
                  path: '/reserve',
                  query: { branch_id: branch?.slug || branch?.id },
                })}
              >
                {t('reserve_table')}
              </Link>
            </Button>
            <Text fontSize='lg' fontWeight={600} mt={3}>
              {t('menu')}
            </Text>
            <Box>
              {menuItems.map((item) => {
                const isActive = activeSection === item.id
                const iconSrc = getImageSrc(item.image, assetBaseUrl)
                const hasChildren = item.children.length > 0

                if (hasChildren) {
                  const isOpen = isSectionOpen(item)
                  return (
                    <Collapsible.Root
                      mb={1}
                      key={item.id}
                      open={isOpen}
                      onOpenChange={({ open }) =>
                        setOpenSections((prev) => ({
                          ...prev,
                          [item.id]: open,
                        }))
                      }
                    >
                      <HStack
                        data-menu-id={item.id}
                        px={3}
                        pt={3}
                        pb={2}
                        gap={2}
                        w='full'
                        rounded={12}
                        alignItems='center'
                        _hover={{ bg: 'white' }}
                        roundedTop={isOpen ? 16 : 8}
                        roundedBottom={isOpen ? 0 : 8}
                        bg={isActive || isOpen ? 'white' : 'transparent'}
                      >
                        {iconSrc && (
                          <Image
                            width={20}
                            height={20}
                            src={iconSrc}
                            alt={item.title}
                          />
                        )}

                        {isNavigateMode && item.slug ? (
                          <Link
                            href={`${linkBase}/${item.slug}`}
                            style={{
                              flex: 1,
                              textDecoration: 'none',
                              color: 'inherit',
                            }}
                          >
                            <Text
                              fontSize='sm'
                              fontWeight={500}
                              textAlign='left'
                            >
                              {item.title}
                            </Text>
                          </Link>
                        ) : item.slug ? (
                          <a
                            href={categoryHref(locale, item.slug)}
                            onClick={(event) =>
                              onInPageCategoryClick(event, () => {
                                if (item.hasSection) onMenuClick?.(item.id)
                                setOpenSections((prev) =>
                                  prev[item.id]
                                    ? prev
                                    : { ...prev, [item.id]: true },
                                )
                                rememberCategoryHash(item.slug!)
                              })
                            }
                            style={{
                              flex: 1,
                              textDecoration: 'none',
                              color: 'inherit',
                            }}
                          >
                            <Text
                              fontSize='sm'
                              fontWeight={500}
                              textAlign='left'
                            >
                              {item.title}
                            </Text>
                          </a>
                        ) : (
                          <Text
                            fontSize='sm'
                            fontWeight={500}
                            flex={1}
                            textAlign='left'
                          >
                            {item.title}
                          </Text>
                        )}

                        <Collapsible.Trigger
                          display='flex'
                          alignItems='center'
                          cursor='pointer'
                          aria-label={item.title}
                        >
                          <Box
                            as='span'
                            transition='transform 0.2s'
                            transform={
                              isOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                            }
                          >
                            <LuChevronRight size={16} />
                          </Box>
                        </Collapsible.Trigger>
                      </HStack>

                      <Collapsible.Content
                        bg={'white'}
                        roundedBottomRight={16}
                        roundedBottomLeft={16}
                      >
                        <Stack
                          p={2}
                          pl={4}
                          mt={2}
                          gap={1}
                          borderLeft='1px'
                          borderColor='border'
                        >
                          {item.children.map((child) => {
                            const childHref =
                              isNavigateMode && child.slug
                                ? `${linkBase}/${child.slug}`
                                : child.slug
                                  ? categoryHref(locale, child.slug)
                                  : `#${child.id}`

                            return (
                              <Text
                                fontSize='sm'
                                fontWeight={500}
                                key={child.id}
                                data-menu-id={child.id}
                                px={3}
                                py={1.5}
                                rounded={8}
                                cursor='pointer'
                                bg={
                                  activeSection === child.id
                                    ? 'primary/10'
                                    : 'transparent'
                                }
                                _hover={{ bg: 'background' }}
                                color={
                                  activeSection === child.id ? 'primary' : 'text'
                                }
                                asChild
                              >
                                {isNavigateMode ? (
                                  <Link href={childHref}>{child.title}</Link>
                                ) : (
                                  <a
                                    href={childHref}
                                    onClick={(event) =>
                                      onInPageCategoryClick(event, () => {
                                        onMenuClick?.(child.id)
                                        if (child.slug) {
                                          rememberCategoryHash(child.slug)
                                        }
                                      })
                                    }
                                  >
                                    {child.title}
                                  </a>
                                )}
                              </Text>
                            )
                          })}
                        </Stack>
                      </Collapsible.Content>
                    </Collapsible.Root>
                  )
                }

                const itemHref =
                  isNavigateMode && item.slug
                    ? `${linkBase}/${item.slug}`
                    : item.slug
                      ? categoryHref(locale, item.slug)
                      : item.hasSection
                        ? `#${item.id}`
                        : undefined

                return (
                  <HStack
                    data-menu-id={item.id}
                    px={3}
                    py={3}
                    gap={2}
                    rounded={12}
                    key={item.id}
                    cursor='pointer'
                    bg={isActive ? 'white' : 'transparent'}
                    opacity={item.hasSection ? 1 : 0.6}
                    asChild
                  >
                    {isNavigateMode && itemHref ? (
                      <Link
                        href={itemHref}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {iconSrc && (
                          <Image
                            width={20}
                            height={20}
                            src={iconSrc}
                            alt={item.title}
                          />
                        )}
                        <Text fontSize='sm' fontWeight={500}>
                          {item.title}
                        </Text>
                      </Link>
                    ) : (
                      <a
                        href={itemHref}
                        onClick={(event) => {
                          if (!item.hasSection) return
                          onInPageCategoryClick(event, () => {
                            onMenuClick?.(item.id)
                            if (item.slug) rememberCategoryHash(item.slug)
                          })
                        }}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {iconSrc && (
                          <Image
                            width={20}
                            height={20}
                            src={iconSrc}
                            alt={item.title}
                          />
                        )}
                        <Text fontSize='sm' fontWeight={500}>
                          {item.title}
                        </Text>
                      </a>
                    )}
                  </HStack>
                )
              })}
            </Box>
          </Stack>
        </Box>
      </StickyBox>
    </Box>
  )
}

function onInPageCategoryClick(
  event: MouseEvent<HTMLAnchorElement>,
  scroll: () => void,
) {
  if (isModifiedClick(event)) return
  event.preventDefault()
  scroll()
}

function InfoRow({
  icon,
  text,
  preserveWhitespace = false,
}: {
  icon: React.ReactNode
  text: React.ReactNode
  preserveWhitespace?: boolean
}) {
  return (
    <HStack align='flex-start' gap='2'>
      <Box>{icon}</Box>
      <Box
        fontSize='sm'
        fontWeight={500}
        lineHeight='shorter'
        whiteSpace={preserveWhitespace ? 'pre-line' : 'normal'}
      >
        {text}
      </Box>
    </HStack>
  )
}
// ⓘ
function getImageSrc(image?: string, baseUrl?: string) {
  if (!image) return undefined

  if (image.startsWith('http')) return image

  return baseUrl
    ? `${baseUrl.replace(/\/$/, '')}/${image.replace(/^\//, '')}`
    : image
}
